import { countOpportunities, MAX_IDS_PER_REQUEST, type OpportunityQuery } from '../../shared/api';
import { isOpportunityId, isPast, localDate, type Opportunity, type WallClock } from '../../shared/opportunity';
import {
  dayBucket,
  FORMATS_FOR,
  matchesOtherFilter,
  OTHER_FILTERS,
  POST_WEEK,
  PRE_WEEK,
  RSVP_TYPES_FOR,
  timeSlotOf,
  WEEK_END,
  WEEK_START,
  type OtherFilter,
  type TimeSlot,
} from '../../shared/taxonomy';

/**
 * What Explore asks the API for, and the "loosen this" suggestion.
 *
 * Every filter is a parameter of `GET /opportunities`, so the list is the pages
 * the API returns. Two things the browser still decides for itself:
 *
 *  - The user's edited copies of listings. The API only knows the listing, so
 *    edited events are left out of every request and checked against the
 *    filters here instead (`matchesLocally`), where the edits are.
 *
 *  - When a search comes back empty, `findLoosenSuggestion` names the single
 *    active filter whose removal recovers the most events ("drop Fashion to
 *    see 41 events"), rather than a dead-end "no events found". It costs one
 *    count per active selection, so it only runs when the result is empty.
 */

export interface FilterState {
  query: string;
  days: Set<string>;
  slots: Set<TimeSlot>;
  /** Goal keys. */
  goals: Set<string>;
  /** Topic keys. */
  topics: Set<string>;
  others: Set<OtherFilter>;
  savedOnly: boolean;
  /**
   * Widen what Explore lists rather than narrow it, so they set the pool the
   * filters run over and don't count as active filters. Reset with the rest.
   */
  showHidden: boolean;
  showPast: boolean;
}

export const EMPTY_FILTERS: FilterState = {
  query: '',
  days: new Set(),
  slots: new Set(),
  goals: new Set(),
  topics: new Set(),
  others: new Set(),
  savedOnly: false,
  showHidden: false,
  // Climate Week is over, so every event is past; there is no toggle to show them.
  showPast: true,
};

export function activeFilterCount(f: FilterState): number {
  return (
    f.days.size +
    f.slots.size +
    f.goals.size +
    f.topics.size +
    f.others.size +
    (f.savedOnly ? 1 : 0) +
    (f.query.trim() ? 1 : 0)
  );
}

export function hasAnyFilter(f: FilterState): boolean {
  return activeFilterCount(f) > 0;
}

/**
 * A stable signature of the filter criteria. The list starts again from a first
 * page on this rather than on anything else, so saving, hiding or editing an
 * event — which changes what is shown but not what was asked — keeps the pages
 * already loaded.
 */
export function filterKey(f: FilterState): string {
  return [
    f.query.trim().toLowerCase(),
    [...f.days].sort().join(','),
    [...f.slots].sort().join(','),
    [...f.goals].sort().join(','),
    [...f.topics].sort().join(','),
    [...f.others].sort().join(','),
    f.savedOnly ? 'saved' : '',
    f.showHidden ? 'hidden' : '',
    f.showPast ? 'past' : '',
  ].join('|');
}

/** Facet identifiers used by the suggestion, so the UI can say what to drop. */
export type FacetKey = 'days' | 'slots' | 'goals' | 'topics' | 'others' | 'query' | 'savedOnly';

export interface LoosenSuggestion {
  facet: FacetKey;
  value: string;
  /** Human label for the thing to drop, e.g. "Fashion". */
  label: string;
  /** How many events come back if it is dropped. */
  gain: number;
}

/** What the browser holds that the filters do not name. */
export interface ListContext {
  /** Marked Not Interested. */
  hiddenIds: ReadonlySet<string>;
  /** Carrying the user's own edits. */
  editedIds: ReadonlySet<string>;
  /** On My Schedule or Save for Later: only under the Saved pill, and never outside it. */
  markedIds: ReadonlySet<string>;
}

/** UUIDs only, and no more than one request can carry. The browser filters the rest. */
const requestIds = (ids: Iterable<string>) => [...ids].filter(isOpportunityId).slice(0, MAX_IDS_PER_REQUEST);

/** The formats or RSVP types every active filter on that axis allows, or undefined if none is active. */
function allowed<T extends string>(others: Set<OtherFilter>, table: Partial<Record<OtherFilter, T[]>>): T[] | undefined {
  let out: T[] | undefined;
  for (const id of OTHER_FILTERS.map((o) => o.id)) {
    const values = others.has(id) ? table[id] : undefined;
    if (!values) continue;
    out = out ? out.filter((v) => values.includes(v)) : [...values];
  }
  return out;
}

/**
 * Just the pool: the past and Not Interested toggles, less the edited events
 * the browser merges in itself, and less the user's saved and scheduled events
 * unless the Saved pill is on. What "M" in "N of M" counts.
 */
export function poolQuery(f: FilterState, ctx: ListContext): OpportunityQuery {
  return {
    includePast: f.showPast || undefined,
    excludeId: requestIds([
      ...(f.showHidden ? [] : ctx.hiddenIds),
      ...(f.savedOnly ? [] : ctx.markedIds),
      ...ctx.editedIds,
    ]),
  };
}

/** The query for one set of filters, or null when it could only come back empty. */
export function listQuery(f: FilterState, ctx: ListContext): OpportunityQuery | null {
  const query: OpportunityQuery = { ...poolQuery(f, ctx) };

  const text = f.query.trim().slice(0, 200);
  if (text) query.q = text;

  const days = [...f.days].sort();
  const day = days.filter((d) => d !== PRE_WEEK && d !== POST_WEEK);
  if (day.length) query.day = day;
  // Pre and Post are "before the 20th" and "after the 27th": one OR group with `day`.
  if (f.days.has(PRE_WEEK)) query.beforeDay = WEEK_START;
  if (f.days.has(POST_WEEK)) query.afterDay = WEEK_END;

  if (f.slots.size) query.timeOfDay = [...f.slots].sort();
  if (f.goals.size) query.goal = [...f.goals].sort();
  if (f.topics.size) query.topic = [...f.topics].sort();

  const formats = allowed(f.others, FORMATS_FOR);
  if (formats) query.format = formats;
  const rsvpTypes = allowed(f.others, RSVP_TYPES_FOR);
  if (rsvpTypes) query.rsvpType = rsvpTypes;

  if (f.savedOnly) {
    const ids = requestIds(ctx.markedIds);
    if (ids.length === 0) return null;
    query.id = ids;
  }

  return query;
}

// --- the same filters, in the browser ------------------------------------------

/** Case- and accent-insensitive, as the API's collation is. */
const fold = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

export const searchTokens = (query: string): string[] => fold(query.trim()).split(/\s+/).filter(Boolean);

/** Everything the API searches: never the organization, which is the same on every row. */
function haystack(e: Opportunity): string {
  return fold(
    [
      e.name,
      e.summary,
      e.description,
      ...e.topics.map((t) => t.label),
      ...e.goals.map((g) => g.label),
      e.address,
      e.neighborhood,
      e.eventType,
    ]
      .filter(Boolean)
      .join(' '),
  );
}

const anyOf = (values: string[], selected: Set<string>) =>
  selected.size === 0 || values.some((v) => selected.has(v));

/** Whether an event belongs in the list, judged here rather than by the API. */
export function matchesLocally(e: Opportunity, f: FilterState, ctx: ListContext, now: WallClock): boolean {
  if (!f.showPast && isPast(e, now)) return false;
  if (!f.showHidden && ctx.hiddenIds.has(e.id)) return false;
  if (f.savedOnly !== ctx.markedIds.has(e.id)) return false;

  const tokens = searchTokens(f.query);
  if (tokens.length) {
    const text = haystack(e);
    if (!tokens.every((t) => text.includes(t))) return false;
  }

  if (f.days.size && !f.days.has(dayBucket(localDate(e)))) return false;
  if (f.slots.size) {
    const slot = timeSlotOf(e);
    if (!slot || !f.slots.has(slot)) return false;
  }
  if (!anyOf(e.goals.map((g) => g.key), f.goals)) return false;
  if (!anyOf(e.topics.map((t) => t.key), f.topics)) return false;
  for (const id of f.others) if (!matchesOtherFilter(e, id)) return false;
  return true;
}

// --- what to loosen --------------------------------------------------------------

export function relax(f: FilterState, facet: FacetKey, value: string): FilterState {
  const next: FilterState = {
    query: f.query,
    days: new Set(f.days),
    slots: new Set(f.slots),
    goals: new Set(f.goals),
    topics: new Set(f.topics),
    others: new Set(f.others),
    savedOnly: f.savedOnly,
    showHidden: f.showHidden,
    showPast: f.showPast,
  };
  switch (facet) {
    case 'days': next.days.delete(value); break;
    case 'slots': next.slots.delete(value as TimeSlot); break;
    case 'goals': next.goals.delete(value); break;
    case 'topics': next.topics.delete(value); break;
    case 'others': next.others.delete(value as OtherFilter); break;
    case 'query': next.query = ''; break;
    case 'savedOnly': next.savedOnly = false; break;
  }
  return next;
}

export interface Labels {
  topicLabel: (key: string) => string;
  goalLabel: (key: string) => string;
}

/**
 * Recounts with each active selection removed, and names the one that recovers
 * the most. Usually two to five counts, all at once.
 *
 * `edited` are the edited copies the browser holds, since the API counts
 * without them.
 */
export async function findLoosenSuggestion(
  f: FilterState,
  ctx: ListContext,
  labels: Labels,
  edited: Opportunity[],
  now: WallClock,
  signal: AbortSignal,
): Promise<LoosenSuggestion | null> {
  const candidates: { facet: FacetKey; value: string; label: string }[] = [];
  for (const v of f.days) candidates.push({ facet: 'days', value: v, label: dayLabel(v) });
  for (const v of f.slots) candidates.push({ facet: 'slots', value: v, label: capitalise(v) });
  for (const v of f.goals) candidates.push({ facet: 'goals', value: v, label: labels.goalLabel(v) });
  for (const v of f.topics) candidates.push({ facet: 'topics', value: v, label: labels.topicLabel(v) });
  for (const v of f.others) candidates.push({ facet: 'others', value: v, label: otherLabel(v) });
  if (f.query.trim()) {
    candidates.push({ facet: 'query', value: f.query, label: `“${f.query.trim()}”` });
  }
  if (candidates.length === 0) return null;

  const totals = await Promise.all(
    candidates.map(async (c) => {
      const relaxed = relax(f, c.facet, c.value);
      const query = listQuery(relaxed, ctx);
      const served = query ? await countOpportunities(query, signal) : 0;
      return served + edited.filter((e) => matchesLocally(e, relaxed, ctx, now)).length;
    }),
  );

  let best: LoosenSuggestion | null = null;
  candidates.forEach((c, i) => {
    const gain = totals[i];
    if (gain > 0 && (!best || gain > best.gain)) best = { ...c, gain };
  });
  return best;
}

function dayLabel(bucket: string): string {
  if (bucket === PRE_WEEK) return 'Pre week';
  if (bucket === POST_WEEK) return 'Post week';
  const [, m, d] = bucket.split('-').map(Number);
  const date = new Date(Date.UTC(2026, m - 1, d));
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function otherLabel(id: string): string {
  return OTHER_FILTERS.find((o) => o.id === id)?.label ?? id;
}

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
